import { ensureItemIds } from '../utils/item-id';
import { normaliseQuotePunctuation } from '../utils/scope-descriptions';
import { mergeIncorporatedBoundaryExclusions } from '../utils/incorporated-boundary-exclusions';
import { resolveQuoteObjective } from './quote-objective';
import { carryRowsMirrorArchive } from '../utils/stage4-stage5-mirror';
import { isNoteClearedByOperator } from '../utils/quote-note-decision';
import {
    authoredAttentionName,
    isAttentionClearedByOperator,
    managingFirmNames,
    resolveDefaultClientCompany,
    resolvePrintedAttention,
    strataOwnersAddresseeApplies,
} from '../utils/tenant-quote-defaults';
import { normalizeStrataPlanNumber, strataPlanFromProjectRef } from '../utils/strata-plan';

function cleanText(value: any) {
    return normaliseQuotePunctuation(String(value || ''))
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function buildDraftSummary({ formData = {}, projectSummary = null }: any = {}) {
    const summaryText = cleanText(
        projectSummary?.executive_summary
        || projectSummary?.executiveSummary
        || projectSummary?.summary
        || projectSummary?.scope_objective
        || projectSummary?.scopeObjective
        || formData.jobDescription
    );

    return {
        title: '1. Project Summary',
        para1: summaryText,
        para2: '',
        bulletTitle: 'Key objectives',
        bullets: [],
        programme: '',
    };
}

/**
 * The Attention line a draft that names a recipient carries, by the Stage 5 editor's own display rule (FullQuoteEditorModal's seed), so
 * the quote saved here reopens showing the same line: the first of [the line, the form's attentionTo, the requester]; under the
 * business's strata-owners setting the first that does not name the managing firm, WHOEVER wrote it (the editor filters a marked line
 * too). `first` is the line itself: the name the operator wrote, or what the quote being replaced shows. Provenance is the caller's:
 * what this returns is the operator's only when it is the operator's own text (C30).
 */
function resolveAttentionLine({ first, previousQuote, formData, tenantProfile, linkedFirmName }: any): string {
    const candidates = [first, formData?.attentionTo, formData?.clientName];
    const strataPlan: any = normalizeStrataPlanNumber(previousQuote?.client?.strataPlan)
        || normalizeStrataPlanNumber(formData?.strataplan)
        || strataPlanFromProjectRef(previousQuote?.header?.projectRef || formData?.strataplan || '');
    if (strataOwnersAddresseeApplies({ tenantProfile, formData, strataPlan })) {
        return resolvePrintedAttention({
            candidates,
            firmNames: managingFirmNames({ firmNames: [linkedFirmName], formData, tenantProfile }),
        });
    }
    return candidates.find(Boolean) || '';
}

export function buildStage5DraftQuoteFromScope({
    scope = [],
    formData = {},
    projectSummary = null,
    quoteNote = '',
    scopeGstInclusive = true,
    projectTitle = '',
    selectedHeroImage = '',
    projectWMS = null,
    // Active tenant profile. Without it resolveDefaultClientCompany applies
    // RJ Bird's strata default to every business.
    tenantProfile = null,
    // The quote this draft replaces, and the open editor's own unsaved Note clear (packet 2A, C15). A provider-failure fallback is
    // saved, so it must not lose a decision the operator already made: an Attention line they emptied, a Note they emptied. Both
    // are inert when absent, so the Stage 4 navigation draft and the prepared peer presentation are unchanged.
    previousQuote = null,
    liveNoteCleared = false,
    liveAttentionCleared = false,
    liveAttention = '',
    linkedFirmName = '',
}: any = {}) {
    if (!Array.isArray(scope) || scope.length === 0) return null;
    // Who the Attention line names (C30). The operator's word beats the requester, and the NEWEST word wins. The open editor's
    // current state travels on its own (liveAttentionCleared, liveAttention: the wrapper admits the whole snapshot only for a builder
    // scope edit, so they cannot ride in previousQuote), and absent / cleared / named are three different states: a name typed after
    // an older saved clear is the current word, and the saved clear must not undo it. Then the saved quote (previousQuote).
    const liveName = typeof liveAttention === 'string' && liveAttention.trim() !== '' ? liveAttention : '';
    const attentionCleared = liveAttentionCleared === true || (!liveName && isAttentionClearedByOperator(previousQuote?.client));
    const authoredAttention = attentionCleared ? '' : (liveName || authoredAttentionName(previousQuote?.client));
    // A draft that REPLACES a saved quote keeps what that quote shows (the editor's display rule, so a recipient saved before the operator
    // mark existed is not swapped for the requester), and the operator's own word goes through the same rule, so the strata-owners
    // filter applies to it too. A draft with no quote behind it takes the requester. The mark describes the line that was CHOSEN: a
    // replacement picked because the operator's text named the managing firm is not their text, and a shown line nobody marked stays
    // unmarked.
    const savedQuote = previousQuote && typeof previousQuote === 'object' && !Array.isArray(previousQuote);
    let attentionLine = formData.clientName || '';
    if (attentionCleared) {
        attentionLine = '';
    } else if (authoredAttention || savedQuote) {
        attentionLine = resolveAttentionLine({
            first: authoredAttention || previousQuote?.client?.attention, previousQuote, formData, tenantProfile, linkedFirmName,
        });
    }
    const attentionIsOperators = authoredAttention !== ''
        && attentionLine !== ''
        && (attentionLine === authoredAttention || attentionLine === authoredAttention.trim());
    const noteCleared = !quoteNote && (liveNoteCleared === true || isNoteClearedByOperator(previousQuote));

    // Stage 4 and Stage 5 are one set of items (Peter, 2026-10-23): the draft
    // shows Stage 4's rows exactly. Display codes, punctuation and client
    // titles are cleaned by the Stage 5 editor when it shows them, not stored
    // in Stage 5 only.
    const sourceScope = ensureItemIds(scope);
    const today = new Date().toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    const generatedAt = new Date().toISOString();
    const title = cleanText(projectTitle);
    // Derive the objective from the pre-sanitised generated evidence. An old
    // strategy title may be the only place that records this is replacement
    // waterproofing or work under an engineering design. The client title is
    // still cleaned below; this evidence-only read prevents that clean-up from
    // silently weakening the objective.
    //
    // The objective is the quote's one objective with no quote yet, from the
    // one resolver over the values Stage 4 reads (Peter, 2026-09-29: Stage 4
    // and Stage 5 must always match). Navigation opens this draft without
    // storing it, so Stage 4 shows this same sentence before Stage 5 opens and
    // after Back. The RFQ text is not an objective: Stage 4 never shows it.
    const scopeObjective = resolveQuoteObjective({ formData, quoteData: null, scope: sourceScope, projectSummary });
    const clientScope = sourceScope;
    const scopeExclusions = clientScope.flatMap((item: any) => (
        Array.isArray(item?.exclusions) ? item.exclusions : []
    )).map((value: any) => String(value || '').trim()).filter(Boolean);

    const draft = {
        header: {
            ...(title ? { title } : {}),
            subtitle: '',
            projectRef: formData.strataplan || formData.address || '',
            heroImage: selectedHeroImage || '',
        },
        meta: {
            quoteNumber: formData.orderNumber || '',
            date: today,
            generatedAt,
            generatedBy: 'deterministic-stage5-draft',
            generationSource: 'stage-4-navigation',
            ...(noteCleared ? { noteClearedByOperator: true } : {}),
        },
        client: {
            name: formData.clientName || '',
            company: resolveDefaultClientCompany({ tenantProfile, formData, strataPlan: formData.strataplan || '' }),
            attention: attentionLine,
            ...(attentionCleared || attentionIsOperators ? { attentionEditedByOperator: true } : {}),
            email: formData.clientEmail || '',
            phone: formData.clientPhone || '',
            address: formData.address || '',
        },
        summary: buildDraftSummary({ formData, projectSummary }),
        scopeObjective,
        scope: clientScope,
        standards: {
            title: '2. Compliance and Standards',
            intro: '',
            list: [],
        },
        exclusions: {
            title: '3. Exclusions',
            items: [...new Set(scopeExclusions)],
            latent: '',
        },
        insurances: {
            title: '4. Insurances',
            items: [],
        },
        terms: {},
        isGstInclusive: !!scopeGstInclusive,
        note: quoteNote || '',
    };
    // The archive of item versions not kept survives a rebuilt draft (review W6).
    return carryRowsMirrorArchive(mergeIncorporatedBoundaryExclusions(draft, projectWMS), previousQuote);
}

export const _internals = {
    buildDraftSummary,
};
